import React, { useState, useEffect } from 'react';
import CardMCFilter from './CardMCFilter';
import { Button, Icon } from 'semantic-ui-react';
import * as XLSX from 'xlsx';
import CardMCVegaChart from './components/CardMCVegaChart';
import { VisualizationSpec } from 'vega-embed';
import SearchBoxDropdown from 'react-lib/appcon/common/SearchBoxDropdown';

const DOCTOR_SEARCH_ID = "MCDoctor";

const thaiMonthsShort = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.'];

type CardMCSummaryPerformanceProps = {
  controller: any;
  runSequence: any;
  MeChartSequence: any;
  searchedItemListWithKey?: Record<string, any>;
}

const CardMCSummaryPerformance = (props: CardMCSummaryPerformanceProps) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(1);
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(12);
  const [viewMode, setViewMode] = useState<
    'rank5_provider_month' | 'rank3_provider_3year' | 'performance_individual'
  >('rank5_provider_month');

  const [doctorSearch, setDoctorSearch] = useState<number | null>(null);

  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const pad = (n: number) => String(n).padStart(2, '0');
      const lastDay = (y: number, m: number) => new Date(y, m, 0).getDate();

      let startDateStr: string;
      let endDateStr: string;

      if (viewMode === 'rank3_provider_3year') {
        startDateStr = `${year - 2}-01-01 00:00:00`;
        endDateStr = `${year}-12-31 23:59:59`;
      } else {
        startDateStr = `${year}-${pad(month)}-01 00:00:00`;
        endDateStr = `${endYear}-${pad(endMonth)}-${lastDay(endYear, endMonth)} 23:59:59`;
      }

      const chartKeyMap: Record<string, string> = {
        rank5_provider_month:  'summary-performance-rank5',
        rank3_provider_3year:  'summary-performance-rank3-year',
        performance_individual:'summary-performance-individual',
      };

      if (props.MeChartSequence?.sequenceIndex === "Action") {
        const res = await props.runSequence({
          sequence: "MeChart",
          action: "QuerySQL",
          chart: chartKeyMap[viewMode],
          startDate: startDateStr,
          endDate: endDateStr,
          ...(viewMode === 'performance_individual' && doctorSearch ? { doctorId: doctorSearch } : {}),
        });

        if (Array.isArray(res) && res?.length > 0 && res?.[0]?.items && Array.isArray(res?.[0]?.items)) {
          setChartData(res[0].items);
        } else {
          setChartData([]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (viewMode === 'performance_individual') return;
    fetchData();
  }, [viewMode, year]);

  const onPrint = () => { window.print(); };

  const onDownload = () => {
    if (!chartData || chartData.length === 0) {
      alert('No data to download');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(chartData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SummaryPerformance");
    XLSX.writeFile(wb, `Summary_Performance_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Data for Vega — derived from chartData
  const getVegaData = () => {
    const colorMap: Record<string, string> = {
      rank5_provider_month:   '#4285F4',
      performance_individual: '#34A853',
    };
    return chartData.map((d: any, i: number) => {
      let label: string;
      let value: number;
      if (viewMode === 'rank5_provider_month') {
        label = [d.first_name, d.middle_name, d.last_name].filter(Boolean).join(' ');
        value = parseInt(d.total_patient || 0);
      } else {
        label = d.label;
        value = parseInt(d.value || 0);
      }
      return { label, value, color: colorMap[viewMode] || '#4285F4', order: i };
    });
  };

  const getVegaRank3YearData = () => {
    const rankLabels: Record<number, string> = {
      1: 'ผู้บริการยอดนิยมอันดับ 1',
      2: 'ผู้บริการยอดนิยมอันดับ 2',
      3: 'ผู้บริการยอดนิยมอันดับ 3',
    };
    return chartData.map((d: any) => ({
      year: String(d.year),
      provider_name: [d.first_name, d.middle_name, d.last_name].filter(Boolean).join(' '),
      rank_position: String(d.rank_position),
      rank_label: rankLabels[d.rank_position] ?? `อันดับ ${d.rank_position}`,
      value: parseInt(d.total_patient || 0),
    }));
  };

  const getSpec = (): VisualizationSpec => {
    if (viewMode === 'rank3_provider_3year') {
      const data = getVegaRank3YearData();
      const rankDomain = ['ผู้บริการยอดนิยมอันดับ 1', 'ผู้บริการยอดนิยมอันดับ 2', 'ผู้บริการยอดนิยมอันดับ 3'];
      const maxValue = data.reduce((m: number, d: any) => Math.max(m, d.value), 0);
      const xDomainMax = Math.ceil(maxValue * 1.8);
      const sharedEncoding: any = {
        y: { field: "year", type: "ordinal", axis: { title: "ปี (Year)" } },
        x: {
          field: "value", type: "quantitative",
          axis: { title: "จำนวนผู้รับบริการ (คน)" },
          scale: { domainMin: 0, domainMax: xDomainMax },
        },
        yOffset: { field: "rank_position", type: "ordinal" },
      };
      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: data },
        encoding: sharedEncoding,
        layer: [
          {
            mark: { type: "bar" as const, tooltip: true },
            encoding: {
              color: {
                field: "rank_label",
                type: "nominal",
                scale: {
                  domain: rankDomain,
                  range: ['#1976D2', '#FF0000', '#32CD32'],
                },
                legend: { title: null },
              },
              tooltip: [
                { field: "year", title: "ปี" },
                { field: "provider_name", title: "ผู้ให้บริการ" },
                { field: "rank_label", title: "อันดับ" },
                { field: "value", title: "จำนวนผู้รับบริการ", format: "," },
              ],
            },
          },
          {
            mark: { type: "text" as const, align: "left", dx: 6, fontSize: 12 },
            encoding: {
              text: { field: "provider_name", type: "nominal" },
            },
          },
        ],
      } as any;
    }

    if (viewMode === 'performance_individual') {
      const data = chartData.map((d: any) => ({
        month: thaiMonthsShort[parseInt(d.month) - 1],
        total_patient: parseInt(d.total_patient || 0),
        order: parseInt(d.month) - 1,
      }));
      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: data },
        mark: { type: "bar", tooltip: true, color: '#34A853' },
        encoding: {
          x: {
            field: "month",
            type: "ordinal",
            axis: { title: "เดือน", labelAngle: -45 },
            sort: { field: "order" },
          },
          y: { field: "total_patient", type: "quantitative", axis: { title: "จำนวนผู้รับบริการ (คน)" } },
          tooltip: [
            { field: "month", title: "เดือน" },
            { field: "total_patient", title: "จำนวนผู้รับบริการ", format: "," },
          ],
        },
      };
    }

    const data = getVegaData();
    const xTitle = viewMode === 'rank5_provider_month' ? 'Total Patients' : 'Score';
    const yTitle = 'Provider';

    return {
      width: "container",
      height: "container",
      autosize: { type: "fit", contains: "padding" },
      data: { values: data },
      mark: { type: "bar", tooltip: true },
      encoding: {
        y: {
          field: "label",
          type: "ordinal",
          axis: { title: yTitle, labelLimit: 200 },
          sort: { field: "order", order: "ascending" }
        },
        x: { field: "value", type: "quantitative", axis: { title: xTitle } },
        color: { field: "color", type: "nominal", scale: null },
        tooltip: [
          { field: "label", title: yTitle },
          { field: "value", title: xTitle, format: "," }
        ]
      }
    };
  };

  return (
    <div id="card-mc-summary-performance">
      {/* Row 1: Toggles */}
      <div className="controls-container">
        <div style={{ maxWidth: '85%' }}>
          <Button.Group vertical={false} style={{ flexWrap: 'wrap' }}>
            <Button
              active={viewMode === 'rank5_provider_month'}
              style={viewMode === 'rank5_provider_month' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setViewMode('rank5_provider_month')}
            >
              ผู้ให้บริการยอดนิยม 5 อันดับในแต่ละเดือน
            </Button>
            <Button
              active={viewMode === 'rank3_provider_3year'}
              style={viewMode === 'rank3_provider_3year' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setViewMode('rank3_provider_3year')}
            >
              ผู้บริการยอดนิยม 3 อันดับ ใน 3 ปีย้อนหลัง
            </Button>
            <Button
              active={viewMode === 'performance_individual'}
              style={viewMode === 'performance_individual' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setViewMode('performance_individual')}
            >
              สรุป Performance ของผู้ให้บริการรายบุคคล
            </Button>
          </Button.Group>
        </div>

        {/* Separate Bar Chart Toggle (Far Right) */}
        <div>
          <Button.Group>
            <Button
              active={true}
              style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }}
              icon
            >
              <Icon name='chart bar' /> Bar
            </Button>
          </Button.Group>
        </div>
      </div>

      {/* Row 2: Filter and Download */}
      <div className="filter-container">
        <CardMCFilter
          mode={viewMode === 'rank3_provider_3year' ? 'yearOnly' : 'default'}
          year={year}
          month={month}
          endYear={endYear}
          endMonth={endMonth}
          onYearChange={setYear}
          onMonthChange={setMonth}
          onEndYearChange={setEndYear}
          onEndMonthChange={setEndMonth}
          onSearch={fetchData}
          onPrint={onPrint}
          searchDisabled={viewMode === 'performance_individual' && !doctorSearch}
          customFilter={
            viewMode === 'performance_individual' ? (
              <SearchBoxDropdown
                onEvent={props.controller.handleEvent}
                type="Doctor"
                id={DOCTOR_SEARCH_ID}
                icon="search"
                limit={20}
                placeholder='ค้นหาผู้ให้บริการ'
                selectedItem={doctorSearch}
                searchedItemListWithKey={props.searchedItemListWithKey}
                useWithKey
                style={{ minWidth: '250px' }}
                mapOptions={(items) => items.filter(Boolean).map((item) => {
                  const name = [item.pre_name, item.first_name, item.last_name].filter(Boolean).join(' ');
                  const content = `${item.code} ${name}`.trim();
                  return { key: item.id, text: content, value: item.id, content };
                })}
                setSelectedItem={(value) => setDoctorSearch(value as number || null)}
              />
            ) : undefined
          }
        >
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' onClick={onDownload}>
            <Icon name='download' />
            Download .xlsx
          </Button>
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' disabled title="Transpose">
            <Icon name='exchange' />
            Transpose
          </Button>
        </CardMCFilter>
      </div>

      {/* Row 4: Chart */}
      <div className="chart-wrapper">
        {loading ? (
          <div>Loading...</div>
        ) : (
          <CardMCVegaChart spec={getSpec()} actions={false} />
        )}
      </div>
    </div>
  );
};

export default CardMCSummaryPerformance;
